import classNames from 'classnames';
import { ButtonType } from './ButtonType';
import { Size } from '../../../constants';
import { ThemeBackground, ThemePrimary, ThemeSecondary, ThemeText } from '../../../context/theme/ThemeConstantsUpdated';

const {
  primaryText,
  primaryTextMuted,
  primaryTextHover,
  primaryTextMutedHover,
  primaryBackground,
  primaryBackgroundHover,
  primaryBackgroundMuted,
  primaryBackgroundMutedHover,
  primaryBackgroundIntense,
  primaryBackgroundIntenseHover,
  primaryFocusOutline,
  primaryTextIntense,
  primaryTextIntenseHover,
} = ThemePrimary;
const { mainText, mainTextHover, mainTextInverted, mainTextInvertedHover, tertiaryText } = ThemeText;
const {
  secondaryBackground,
  secondaryBackgroundHover,
  secondaryText,
  secondaryTextHover,
  secondaryBackgroundMuted,
  secondaryBackgroundMutedHover,
  secondaryTextIntense,
} = ThemeSecondary;
const { mainBackgroundInverted, mainBackgroundInvertedHover } = ThemeBackground;

export const getButtonColorClasses = (options: { isDarkMode?: boolean; buttonType?: ButtonType }) => {
  const { isDarkMode } = options;
  const buttonType = options.buttonType || ButtonType.Primary;

  let classes = '';

  switch (buttonType) {
    case ButtonType.Primary:
      classes = classNames(primaryBackground, primaryBackgroundHover, primaryFocusOutline, 'text-white');
      break;
    case ButtonType.PrimaryDark:
      classes = classNames(
        isDarkMode
          ? [primaryBackgroundMuted, primaryBackgroundMutedHover]
          : [primaryBackgroundIntense, primaryBackgroundIntenseHover],
        primaryFocusOutline,
        'text-white'
      );
      break;
    case ButtonType.Secondary:
      classes = classNames(
        primaryBackgroundMuted,
        primaryBackgroundMutedHover,
        primaryFocusOutline,
        primaryText,
        primaryTextHover
      );
      break;
    case ButtonType.Alert:
      classes = classNames('text-white', primaryFocusOutline, secondaryBackground, secondaryBackgroundHover);
      break;
    case ButtonType.AlertSecondary:
      classes = classNames(
        primaryFocusOutline,
        secondaryBackgroundMuted,
        secondaryBackgroundMutedHover,
        secondaryTextIntense
      );
      break;
    case ButtonType.Light:
      classes = classNames('bg-white hover:bg-gray-100 text-black', primaryFocusOutline);
      break;
    case ButtonType.Dark:
      classes = classNames(
        'bg-gray-900 hover:bg-gray-700 text-white',
        primaryBackgroundMutedHover,
        primaryFocusOutline
      );
      break;
    case ButtonType.Inverted:
      classes = classNames(
        mainBackgroundInverted,
        mainBackgroundInvertedHover,
        mainTextInverted,
        mainTextInvertedHover
      );
      break;
    case ButtonType.Confirm:
      classes = `bg-blue-600 hover:bg-blue-500 focus-visible:outline-blue-600 text-white`;
      break;
    case ButtonType.Success:
      classes = `bg-green-600 hover:bg-green-500 focus-visible:outline-green-600 text-white`;
      break;
    case ButtonType.Danger:
      classes = `bg-red-600 hover:bg-red-500 focus-visible:outline-red-600 text-white`;
      break;
    case ButtonType.Link:
      classes = classNames(
        mainText,
        mainTextHover,
        'bg-transparent hover:bg-transparent hover:text-underline focus:outline-none shadow-0 border-0'
      );
      break;
    case ButtonType.LinkLight:
      classes =
        'text-white hover:text-gray-100 bg-transparent hover:bg-transparent hover:text-underline focus:outline-none shadow-0 border-0';
      break;
    case ButtonType.LinkAlert:
      classes = classNames(
        secondaryText,
        secondaryTextHover,
        'bg-transparent hover:bg-transparent hover:text-underline focus:outline-none shadow-0 border-0'
      );
      break;
    case ButtonType.SecondaryLink:
      classes = classNames(
        tertiaryText,
        mainTextHover,
        'bg-transparent hover:bg-transparent hover:text-underline focus:outline-none shadow-0 border-0'
      );
      break;
    case ButtonType.PrimaryLink:
      classes = classNames(
        primaryText,
        primaryTextHover,
        'bg-transparent hover:bg-transparent hover:text-underline focus:outline-none shadow-0 border-0'
      );
      break;
    case ButtonType.DangerLink:
      classes = classNames(
        'text-red-600',
        'hover:text-red-500',
        'bg-transparent hover:bg-transparent hover:text-underline focus:outline-none shadow-0 border-0'
      );
      break;
    case ButtonType.PrimaryLinkLight:
      classes = classNames(
        primaryTextMuted,
        primaryTextMutedHover,
        primaryBackgroundMutedHover,
        'bg-transparent hover:bg-transparent hover:text-underline focus:outline-none shadow-0 border-0'
      );
      break;
  }

  return classes;
};

export const getButtonSizeClasses = (
  size: Size.sm | Size.md | Size.lg | Size.xl,
  isLink?: boolean
): ButtonSizeClasses => {
  let buttonSizeClasses: string;
  let iconButtonSizeClasses: string;
  let iconSizeClasses: string;

  switch (size) {
    case Size.sm:
      buttonSizeClasses = isLink ? 'py-1' : 'py-1 px-2';
      iconButtonSizeClasses = 'h-7 w-7';
      iconSizeClasses = 'h-5 w-5';
      break;
    case Size.md:
      buttonSizeClasses = isLink ? 'py-2' : 'py-2 px-3';
      iconButtonSizeClasses = 'h-9 w-9';
      iconSizeClasses = 'h-5 w-5';
      break;
    case Size.lg:
      buttonSizeClasses = isLink ? 'py-2.5' : 'py-2.5 px-4';
      iconButtonSizeClasses = 'h-9 w-9';
      iconSizeClasses = 'h-6 w-6';
      break;
    case Size.xl:
      buttonSizeClasses = isLink ? 'py-3' : 'py-3 px-5';
      iconButtonSizeClasses = 'h-11 w-11';
      iconSizeClasses = 'h-6 w-6';
      break;
  }

  return {
    buttonSizeClasses,
    iconButtonSizeClasses,
    iconSizeClasses,
  };
};

export function removeHoverClasses(classNames: string) {
  return classNames
    .split(' ')
    .filter((className) => !className.startsWith('hover:'))
    .join(' ');
}

type ButtonSizeClasses = {
  buttonSizeClasses: string;
  iconButtonSizeClasses: string;
  iconSizeClasses: string;
};

export const BUTTON_ANIMATION = {
  whileHover: { scale: 1.1 },
  whileTap: { scale: 0.9 },
};
